import { Pagination } from '@/domain/entities/Pagination'
import {
  ManageCourier,
  ManageCourierAdjustSaldo,
  ManageCourierBankInformation,
  ManageCourierDetail,
  ManageCouriers,
  ManageCouriersAdjustSaldo,
  Partner,
} from '@/domain/entities/ManageCourier'
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators'
import store from '@/app/ui/store'
import { container } from 'tsyringe'
import { ManageCourierPresenter } from '@/app/ui/presenters/ManageCourierPresenter'
import { Utils } from '@/app/infrastructures/misc'
import Vue from 'vue'
import {
  AddCourierRequest,
  EditCourierRequest,
} from '@/data/payload/api/ManageCourierRequest'
import router from '../router'

export interface ManageCourierState {
  isLoading: boolean
  isLoadingExport: boolean
  paginationData: Pagination
  manageCourierData: ManageCourier[]
  manageCourierAdjustSaldoData: ManageCourierAdjustSaldo[]
  manageCourierDetail: ManageCourierDetail
  partnerData: Partner[]
}

export interface HasChangedType {
  driverName: boolean
  phoneNumber: boolean
  courierType: boolean
  announcementCourierType: boolean
  vehicleType: boolean
  vehiclePlate: boolean
  partnerName: boolean
  bankAccountName: boolean
  bankName: boolean
  bankAccount: boolean
}

export interface FormType {
  driverName: string
  phoneNumber: string
  vehiclePlate: string
  bankAccountName: string
  bankAccount: string
}

@Module({ namespaced: true, store, name: 'manageCourier', dynamic: true })
class ManageCourierController extends VuexModule implements ManageCourierState {
  private presenter: ManageCourierPresenter = container.resolve(
    ManageCourierPresenter
  )
  public isLoading = false
  public isLoadingExport = false
  public paginationData = new Pagination(1, 100)
  public manageCourierData = [new ManageCourier()]
  public manageCourierAdjustSaldoData = [new ManageCourierAdjustSaldo()]
  public manageCourierDetail = new ManageCourierDetail(
    0,
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    new ManageCourierBankInformation('', '', '', ''),
    ''
  )
  public partnerData = [new Partner()]
  public modalSuccessAdd = false
  public modalSuccessEdit = false

  @Action({ rawError: true })
  public getManageCourierList(params: Record<string, string | number>): void {
    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getAll(formattedParams)
      .then(res => {
        this.setManageCourierData(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Manage Courier Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getCourierDetail(form: { id: string }): void {
    this.setLoading(true)

    this.presenter
      .getDetail(form.id)
      .then(res => {
        this.setCourierDetail(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Courier Detail Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public createCourier(form: {
    fullname: string
    phoneNumber: string
    courierType: string
    vehicleType: string
    vehiclePlate: string
    partnerName: string
    bankAccountName: string
    bankId: string
    bankAccount: string
    announcementCourierType: string
  }): void {
    this.setLoading(true)

    this.presenter
      .addCourier(
        new AddCourierRequest(
          form.fullname,
          form.phoneNumber,
          form.courierType,
          form.vehicleType,
          form.vehiclePlate,
          form.partnerName,
          form.bankAccountName,
          form.bankId,
          form.bankAccount,
          form.announcementCourierType
        )
      )
      .then(() => {
        router.push('/manage-user-&-courier/manage-courier')
        this.setModalSuccess(true)
      })
      .catch(error => {
        Vue.notify({
          title: 'Add Courier Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public updateCourier(form: {
    courierId: string
    fullname: string
    phoneNumber: string
    courierType: string
    courierStatus: string
    vehicleType: string
    vehiclePlate: string
    partnerName: string
    bankAccountName: string
    bankId: string
    bankAccount: string
    announcementCourierType: string
  }): void {
    this.setLoading(true)

    this.presenter
      .editCourier(
        form.courierId,
        new EditCourierRequest(
          form.fullname,
          form.phoneNumber,
          form.courierType,
          form.courierStatus,
          form.vehicleType,
          form.vehiclePlate,
          form.partnerName,
          form.bankAccountName,
          form.bankId,
          form.bankAccount,
          form.announcementCourierType
        )
      )
      .then(() => {
        this.setModalSuccessEdit(true)
      })
      .catch(error => {
        Vue.notify({
          title: 'Update Courier Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getManageCourierListAdjustSaldo(
    params: Record<string, string | number>
  ): void {
    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getCouriersAdjustSaldo(formattedParams)
      .then(res => {
        this.setManageCourierAdjustSaldoData(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Manage Courier Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getAllPartner(params: Record<string, string | number>): void {
    this.setLoading(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getAllPartner(formattedParams)
      .then(res => {
        if (res) this.setPartnerData(res)
      })
      .catch(error => {
        if (params.search) {
          Vue.notify({
            title: 'Fetch Partner List Failed',
            text:
              error.status === 400 || error.status === 422
                ? error.error.message.en
                : 'Something wrong',
            type: 'error',
            duration: 5000,
          })
        }
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public export(params: Record<string, string | number>) {
    this.setLoadingExport(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .export(formattedParams)
      .then(res => {
        const anchor = document.createElement('a')
        anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(res)
        anchor.target = '_blank'
        anchor.download = 'Manage Courier.csv'
        anchor.click()
      })
      .catch(error => {
        Vue.notify({
          title: 'Export Manage Courier Failed',
          text: [400, 422].includes(error.status)
            ? error.error.message.en
            : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoadingExport(false)
      })
  }

  @Mutation
  private setManageCourierData(manageCouriers: ManageCouriers): void {
    this.paginationData = <Pagination>manageCouriers.pagination
    this.manageCourierData = <ManageCourier[]>manageCouriers.data
  }

  @Mutation
  private setPartnerData(partners: Partner[]): void {
    this.partnerData = <Partner[]>partners
  }

  @Mutation
  private setManageCourierAdjustSaldoData(
    manageCouriersAdjustSaldo: ManageCouriersAdjustSaldo
  ): void {
    this.paginationData = <Pagination>manageCouriersAdjustSaldo.pagination
    this.manageCourierAdjustSaldoData = <ManageCourierAdjustSaldo[]>(
      manageCouriersAdjustSaldo.data
    )
  }

  @Mutation
  private setCourierDetail(manageCourierDetail: ManageCourierDetail): void {
    this.manageCourierDetail = manageCourierDetail
  }

  @Mutation
  private setLoading(bool: boolean): void {
    this.isLoading = bool
  }

  @Mutation
  private setLoadingExport(bool: boolean): void {
    this.isLoadingExport = bool
  }

  @Mutation
  public setModalSuccess(bool: boolean): void {
    this.modalSuccessAdd = bool
  }

  @Mutation
  public setModalSuccessEdit(bool: boolean): void {
    this.modalSuccessEdit = bool
  }
}

export default getModule(ManageCourierController)
